import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { postAsync, clearAuthFromApi } from "../../../api/Api";
import { __DEV__ } from "@/utils/constants";
import { logout } from "../userSlice/UserSlice";

export interface Airport {
  placeId: string;
  placeName: string;
  countryId: string;
  countryName: string;
  cityId: string;
  cityName: string;
  iata: string;
  /** API may return iataCode instead of or in addition to iata */
  iataCode?: string;
  entityId: string;
  type: string;
  /** API returns `name` (e.g. airport name or city name) – distinct from `placeName`/`cityName`. */
  name?: string;
}

export interface PlaceAutosuggestResponse {
  returnCode: number;
  returnMessage: string;
  places: Airport[];
}

export interface FlightDate {
  year: number;
  month: number;
  day: number;
}

export interface FlightDateRangeMonth {
  year: number;
  month: number;
}

export interface FlightDateRange {
  startDate: FlightDateRangeMonth;
  endDate: FlightDateRangeMonth;
}

export type QueryLeg =
  | {
      originPlaceId: string;
      destinationPlaceId: string;
      date: FlightDate;
      dateRange?: never;
    }
  | {
      originPlaceId: string;
      destinationPlaceId: string;
      dateRange: FlightDateRange;
      date?: never;
    };

export type CabinClass =
  | "CABIN_CLASS_UNSPECIFIED"
  | "CABIN_CLASS_ECONOMY"
  | "CABIN_CLASS_PREMIUM_ECONOMY"
  | "CABIN_CLASS_BUSINESS"
  | "CABIN_CLASS_FIRST";

export interface FlightSearchRequest {
  queryLegs: QueryLeg[];
  cabinClass: CabinClass;
  adults: number;
  childrenAges: number[];
}

export enum SkyscannerResponseCode {
  SUCCESS = 0,
  FAILURE = 1,
  POLL_CONTINUE = 2,
}

/** API may return numeric codes as strings; Redux/UI use strict equality on the enum. */
export function normalizeSkyscannerReturnCode(
  raw: unknown
): SkyscannerResponseCode | null {
  const n =
    typeof raw === "string" && raw.trim() !== ""
      ? parseInt(raw, 10)
      : Number(raw);
  if (!Number.isFinite(n)) return null;
  if (
    n === SkyscannerResponseCode.SUCCESS ||
    n === SkyscannerResponseCode.FAILURE ||
    n === SkyscannerResponseCode.POLL_CONTINUE
  ) {
    return n as SkyscannerResponseCode;
  }
  return null;
}

export type PlaceType =
  | "PLACE_TYPE_CITY"
  | "PLACE_TYPE_COUNTRY"
  | "PLACE_TYPE_AIRPORT";

export type AgentType =
  | "AGENT_TYPE_TRAVEL_AGENT"
  | "AGENT_TYPE_AIRLINE";

export type PriceUnit =
  | "PRICE_UNIT_UNSPECIFIED"
  | "PRICE_UNIT_WHOLE"
  | "PRICE_UNIT_CENTI"
  /** Amount is in thousandths of the currency unit (divide by 1000). */
  | "PRICE_UNIT_MILLI";

export type PriceUpdateStatus =
  | "PRICE_UPDATE_STATUS_UNSPECIFIED"
  | "PRICE_UPDATE_STATUS_PENDING"
  | "PRICE_UPDATE_STATUS_CURRENT";

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface FlightPlace {
  entityId: string;
  parentId: string;
  name: string;
  type: PlaceType;
  iata: string;
  coordinates: Coordinates | null;
}

export interface Agent {
  name: string;
  type: AgentType;
  imageUrl: string;
  feedbackCount: number;
  rating: number;
  isOptimisedForMobile: boolean;
  /** Catalog / Pay affiliate id when the flight-search API includes it */
  affiliateId?: number;
}

export interface Price {
  amount: string;
  unit: PriceUnit;
  updateStatus: PriceUpdateStatus;
}

export interface CountWithMinPrice {
  count: number;
  minPrice: Price;
}

export interface TicketTypeStats {
  singleTicket: CountWithMinPrice;
  multiTicketNonNpt: CountWithMinPrice;
  multiTicketNpt: CountWithMinPrice;
}

export interface StopStats {
  total: CountWithMinPrice;
  ticketTypes: TicketTypeStats;
}

export interface ItineraryStats {
  minDuration: number;
  maxDuration: number;
  total: CountWithMinPrice;
  stops: {
    direct: StopStats;
    oneStop: StopStats;
    twoPlusStops: StopStats;
  };
  hasChangeAirportTransfer: boolean;
}

export interface FlightSearchStats {
  itineraries: ItineraryStats;
}

export interface SortingOptionEntry {
  score: number;
  itineraryId: string;
}

export interface SortingOptions {
  best: SortingOptionEntry[];
  cheapest: SortingOptionEntry[];
  fastest: SortingOptionEntry[];
}

export interface FlightSearchResults {
  itineraries: Record<string, any>;
  legs: Record<string, any>;
  segments: Record<string, any>;
  places: Record<string, FlightPlace>;
  carriers: Record<string, any>;
  agents: Record<string, Agent>;
  alliances: Record<string, any>;
  fareAttributeFilters: Record<string, any>;
}

export interface FlightSearchResultsWrapper {
  results: FlightSearchResults;
  stats: FlightSearchStats;
  sortingOptions: SortingOptions;
  /** Session/click id from flight-search / poll-search for save-ts-click */
  feClickId?: string;
}

export interface FlightSearchResponse {
  returnCode: SkyscannerResponseCode;
  returnMessage: string;
  results: FlightSearchResultsWrapper;
  /** May also be sent at response root (merged into stored wrapper in slice) */
  feClickId?: string;
}

export const getPlaceAutosuggest = createAsyncThunk<
  PlaceAutosuggestResponse,
  string
>("flightsStore/getPlaceAutosuggest", async (feSearchTerm: string, thunkAPI) => {
  const data = {
    url: "/cmd/flights/get-place-autosuggest",
    body: { feSearchTerm },
  };

  try {
    const response = await postAsync(data);
    return response as PlaceAutosuggestResponse;
  } catch (error: any) {
    __DEV__ &&
      console.log("[FlightsApi/getPlaceAutosuggest] rejected", {
        message: error?.message,
        isAxios: axios.isAxiosError(error),
        status: error?.response?.status,
        responseData: error?.response?.data,
        code: error?.code,
      });

    let is401 = false;
    let parse401FromMessage = false;
    try {
      const parsed = JSON.parse(error.message);
      if (parsed.status === 401) {
        is401 = true;
        parse401FromMessage = true;
      }
    } catch {
      /* error.message is not JSON (e.g. raw Axios after refresh retry) */
    }

    __DEV__ &&
      console.log("[FlightsApi/getPlaceAutosuggest] 401 handling", {
        is401,
        parse401FromMessage,
        looksLikeAxios401:
          axios.isAxiosError(error) && error.response?.status === 401,
      });

    if (is401) {
      thunkAPI.dispatch(logout());
      clearAuthFromApi();
    }

    throw error;
  }
});

export const flightSearch = createAsyncThunk<
  FlightSearchResponse,
  FlightSearchRequest
>("flightsStore/flightSearch", async (input: FlightSearchRequest) => {
  const data = {
    url: "/cmd/flights/flight-search",
    body: input,
  };

  const response = await postAsync(data);
  return response as FlightSearchResponse;
});

export const pollFlightSearch = createAsyncThunk<
  FlightSearchResponse,
  void
>("flightsStore/pollFlightSearch", async () => {
  const data = {
    url: "/cmd/flights/poll-search",
    body: {},
  };

  const response = await postAsync(data);
  return response as FlightSearchResponse;
});
